import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Star, MapPin } from 'lucide-react';
import AppImage from 'components/AppImage';

const HeroSection = ({ onBookNow }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const featuredExperiences = [
    {
      id: 1,
      title: 'Ultimate White Water Adventure',
      description: 'Experience the thrill of Grade III rapids with expert guides',
      image: '/assets/images/hero-rafting.jpg',
      price: 1899,
      rating: 4.8,
      location: 'Kali River, Dandeli'
    },
    {
      id: 2,
      title: 'Jungle Safari Expedition',
      description: 'Discover exotic wildlife in their natural habitat',
      image: '/assets/images/hero-safari.jpg',
      price: 1299,
      rating: 4.6,
      location: 'Dandeli Wildlife Sanctuary'
    },
    {
      id: 3,
      title: 'Canopy Zip Line Thrills',
      description: 'Soar through the forest canopy with breathtaking views',
      image: '/assets/images/hero-zipline.jpg',
      price: 1599,
      rating: 4.9,
      location: 'Forest Reserve, Dandeli'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredExperiences?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Slides */}
      <div className="absolute inset-0">
        {featuredExperiences?.map((experience, index) => (
          <motion.div
            key={experience?.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            initial={{ scale: 1.1 }}
            animate={{ scale: index === currentSlide ? 1 : 1.1 }}
            transition={{ duration: 1 }}
          >
            <AppImage
              src={experience?.image}
              alt={experience?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </motion.div>
        ))}
      </div>
      {/* Content Overlay */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Logo and Branding */}
              <div className="mb-8">
                <AppImage
                  src="/assets/images/Logo-1758034090926.png"
                  alt="Dandeli Adventure Resorts"
                  className="h-20 mx-auto mb-4"
                />
                <h1 className="text-6xl md:text-7xl font-bold font-['Playfair Display',serif] mb-4">
                  Featured Experiences Hub
                </h1>
                <div className="w-32 h-1 bg-green-400 mx-auto rounded-full"></div>
              </div>

              {/* Current Experience Info */}
              <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 font-['Poppins',sans-serif]">
                  {featuredExperiences?.[currentSlide]?.title}
                </h2>
                <p className="text-xl mb-6 text-gray-200">
                  {featuredExperiences?.[currentSlide]?.description}
                </p>
                
                <div className="flex items-center justify-center space-x-8 mb-6">
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5 text-yellow-400 fill-current" />
                    <span className="font-semibold">{featuredExperiences?.[currentSlide]?.rating}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-green-400" />
                    <span>{featuredExperiences?.[currentSlide]?.location}</span>
                  </div>
                  <div className="text-2xl font-bold text-green-400">
                    ₹{featuredExperiences?.[currentSlide]?.price}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    onClick={() => onBookNow(featuredExperiences?.[currentSlide])}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Now - Just ₹{featuredExperiences?.[currentSlide]?.price}/-
                  </motion.button>
                  
                  <motion.button
                    className="bg-white bg-opacity-20 backdrop-blur-sm hover:bg-opacity-30 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 border border-white border-opacity-30 flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="h-5 w-5" />
                    <span>Watch Demo</span>
                  </motion.button>
                </div>
              </div>

              {/* Marketing Lines */}
              <div className="mt-8">
                <motion.div
                  className="inline-block bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  🔥 Limited Time Offer - Book now just at ₹1499/-
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {featuredExperiences?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white scale-125' :'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
            />
          ))}
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-20">
        <motion.div
          className="text-white text-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="text-sm mb-2">Scroll to explore</div>
          <div className="w-1 h-8 bg-white bg-opacity-50 mx-auto rounded-full"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;